import { useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useFrame } from '@react-three/fiber'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { createInstancedAssetGroup } from '../../../utils/instancedAsset'
import {
    lanePositions,
    powerUpScaleStartDistance,
    powerUpScaleEndDistance,
} from '../../config/constants'
import type { TrackController } from '../track/trackController'
import { useRunState } from '../../provider/useRunState'
import { buildPowerUpCollisionData, checkPowerUpCollision } from './powerUpCollision'
import { useTrackController } from '../track/useTrackController'
import type { PowerUpType } from '@/src/stores/runStateTypes'
import { untracked } from 'mobx'
import { POWER_UP_CONFIG } from './powerUpConfig'
import { getDustSpriteTexture } from './powerUpAssets'
import { gltfAsset } from '../../assetStore'
import { dispatchPowerUpCollected } from './powerUpEvents'
import { usePowerUpIconAtlasTexture } from './powerUpTextures'
import {
    collectAnimScale,
    getCollectablePowerUpPlacements,
    resolvePowerUpYOffset,
    type IndexedPowerUpPlacement,
} from './powerUpHelpers'
import { disposeObject, markForDisposal } from '../../../utils/threeDisposal'

const SIMPLE_TYPES = ['heart', 'coin'] as const
type SimpleType = typeof SIMPLE_TYPES[number]
const SIMPLE_TYPE_PHASE_OFFSET: Record<SimpleType, number> = {
    heart: 0,
    coin: Math.PI,
}

const GROUND_COLLECT_THRESHOLD = 0.3
const BASE_SCALE = POWER_UP_CONFIG.baseScale
const IDLE_ROTATION_SPEED = POWER_UP_CONFIG.idleRotationSpeed
const IDLE_BOB_AMPLITUDE = POWER_UP_CONFIG.idleBobAmplitude
const IDLE_BOB_SPEED = POWER_UP_CONFIG.idleBobSpeed
const IDLE_TRACK_ROTATION_OFFSET = POWER_UP_CONFIG.idleTrackRotationOffset
const COLLECT_DURATION = POWER_UP_CONFIG.collectDuration
const COLLECT_ROTATION_TURNS = POWER_UP_CONFIG.collectRotationTurns
const ICON_MODEL_Y = 2.3713
const SIMPLE_COLLECT_DURATION = COLLECT_DURATION * 0.35
const SIMPLE_IDLE_ROTATION_OFFSET = THREE.MathUtils.degToRad(90)
const SIMPLE_IDLE_ROTATION_SWING = THREE.MathUtils.degToRad(45)

const PARTICLE_SETTINGS = {
    count: POWER_UP_CONFIG.particleCount,
    size: POWER_UP_CONFIG.particleSize,
    opacity: POWER_UP_CONFIG.particleOpacity,
    speed: POWER_UP_CONFIG.particleSpeed,
    spread: POWER_UP_CONFIG.particleSpread,
    lifetime: POWER_UP_CONFIG.particleLifetime,
    upwardBias: POWER_UP_CONFIG.particleUpwardBias,
    drag: POWER_UP_CONFIG.particleDrag,
}

const SIMPLE_POWERUP_COLORS: Record<SimpleType, string> = {
    heart: '#ff5252',
    coin: '#ffd54a',
}

const SIMPLE_POWERUP_MODELS: Record<SimpleType, string> = {
    heart: gltfAsset.Heart,
    coin: gltfAsset.Coin,
}

type SimpleInstance = {
    type: SimpleType
    position: number
    worldPosition: THREE.Vector3
    worldRotation: THREE.Quaternion
    collectTimer: number
}

type ParticleBurst = {
    age: number
    points: THREE.Points
    geometry: THREE.BufferGeometry
    material: THREE.PointsMaterial
    positions: Float32Array
    velocities: Float32Array
}

type FlashSphere = {
    age: number
    mesh: THREE.Mesh
    material: THREE.MeshBasicMaterial
    duration: number
    maxScale: number
}

type SimpleController = {
    group: THREE.Group
    instances: SimpleInstance[]
    iconMeshesByType: Map<SimpleType, THREE.InstancedMesh[]>
    instanceIndexesByType: Map<SimpleType, number[]>
    spawnCollectParticles: (instanceIndex: number, color: string) => void
    update: (trackPosition: number, delta: number, cameraQuaternion: THREE.Quaternion) => void
}

function prepareSimpleAsset(
    sourceScene: THREE.Object3D,
    type: SimpleType,
    iconAtlas: THREE.Texture,
): THREE.Object3D {
    const cloned = sourceScene.clone(true)

    const iconNodeName = type === 'heart' ? 'Heart' : 'Coin'
    const iconNode = cloned.getObjectByName(iconNodeName)
    if (iconNode instanceof THREE.Mesh) {
        const material = new THREE.MeshBasicMaterial({
            map: iconAtlas,
            transparent: true,
            alphaTest: 0.1,
            depthWrite: true,
            toneMapped: false,
            side: THREE.DoubleSide,
        })
        markForDisposal(material)
        iconNode.material = material
        iconNode.removeFromParent()
    }

    return iconNode instanceof THREE.Object3D ? iconNode : new THREE.Group()
}

export const CollectablesController = observer(function CollectablesController() {
    const trackController = useTrackController()
    const runState = useRunState()
    const iconAtlas = usePowerUpIconAtlasTexture()

    const { scene: heartScene } = useGLTF(SIMPLE_POWERUP_MODELS.heart)
    const { scene: coinScene } = useGLTF(SIMPLE_POWERUP_MODELS.coin)

    const scenes: Record<SimpleType, THREE.Object3D> = {
        heart: heartScene,
        coin: coinScene,
    }

    const simplePlacements = useMemo(() => getCollectablePowerUpPlacements(), [])

    const collisionData = useMemo(
        () => buildPowerUpCollisionData(simplePlacements, trackController),
        [simplePlacements, trackController],
    )

    const { group, instances, spawnCollectParticles, update } = useMemo(
        () => createSimpleController(scenes, simplePlacements, trackController, iconAtlas),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [heartScene, coinScene, trackController, iconAtlas],
    )
    useEffect(() => {
        return () => {
            disposeObject(group)
        }
    }, [group])

    useEffect(() => {
        update(trackController.getTrackPlaybackState().trackPosition, 0, new THREE.Quaternion())
    }, [update, trackController])

    // respawn every collectible whenever a reset/restart bumps the token.
    const collectiblesResetToken = runState.collectiblesResetToken
    useEffect(() => {
        collisionData.powerUps.forEach((powerUp) => {
            powerUp.collected = false
        })
        instances.forEach((instance) => {
            instance.collectTimer = -1
        })
    }, [collectiblesResetToken, collisionData, instances])

    useFrame(({ camera }, delta) => {
        const trackPosition = trackController.getTrackPlaybackState().trackPosition
        update(trackPosition, delta, camera.quaternion)

        if (runState.phase !== 'running') return
        if (runState.snapshot.hitReactionTimer > 0) return

        const snapshot = runState.snapshot

        const hitIndex = checkPowerUpCollision(
            trackPosition,
            snapshot.laneVisualX,
            collisionData,
        )

        if (hitIndex === -1) return

        const placement = simplePlacements[hitIndex]

        const jumpVisualOffset = snapshot.characterVisualPosition[1]
        const isGroundLevel = jumpVisualOffset < GROUND_COLLECT_THRESHOLD

        if (Boolean(placement.up) === isGroundLevel) return

        untracked(() => {
            collisionData.powerUps[hitIndex].collected = true
            instances[hitIndex].collectTimer = 0
            spawnCollectParticles(hitIndex, SIMPLE_POWERUP_COLORS[placement.type as SimpleType])
        })

        runState.activatePowerUp(placement.type as PowerUpType)
        dispatchPowerUpCollected(placement.powerUpIndex)

        if (placement.type === 'coin') {
            window.dispatchEvent(new CustomEvent('coin-collect'))
        } else if (placement.type === 'heart') {
            window.dispatchEvent(new CustomEvent('heart-collect'))
        }
    })

    return <primitive object={group} />
})

function createSimpleController(
    scenes: Record<SimpleType, THREE.Object3D>,
    placements: IndexedPowerUpPlacement[],
    trackController: TrackController,
    iconAtlas: THREE.Texture,
): SimpleController {

    const yRotation = new THREE.Quaternion()
    const finalRotation = new THREE.Quaternion()
    const Y_AXIS = new THREE.Vector3(0, 1, 0)
    const _iconPosition = new THREE.Vector3()
    const _particleDir = new THREE.Vector3()
    let idleElapsed = 0

    const instances: SimpleInstance[] = placements.map((placement) => {
        const frame = trackController.frameAtPosition(placement.position)
        const rotation = frame.cameraQuaternion.clone()
        const offset = new THREE.Vector3(
            lanePositions[placement.track],
            resolvePowerUpYOffset(placement.up),
            0,
        ).applyQuaternion(rotation)

        return {
            type: placement.type as SimpleType,
            position: placement.position,
            worldPosition: frame.position.clone().add(offset),
            worldRotation: rotation,
            collectTimer: -1,
        }
    })

    const rootGroup = new THREE.Group()
    const iconMeshesByType = new Map<SimpleType, THREE.InstancedMesh[]>()
    const instanceIndexesByType = new Map<SimpleType, number[]>()
    const matrix = new THREE.Matrix4()
    const scale = new THREE.Vector3()

    for (const type of SIMPLE_TYPES) {
        const indicesForType = placements.flatMap((p, i) =>
            p.type === type ? [i] : [],
        )
        if (indicesForType.length === 0) continue

        const iconAsset = prepareSimpleAsset(scenes[type], type, iconAtlas)

        const initialZeroMatrices = indicesForType.map((globalIndex) =>
            new THREE.Matrix4().compose(
                instances[globalIndex].worldPosition,
                instances[globalIndex].worldRotation,
                new THREE.Vector3(0, 0, 0),
            ),
        )

        const iconGroup = createInstancedAssetGroup(iconAsset, initialZeroMatrices)
        const iconMeshes = iconGroup.children.filter(
            (child): child is THREE.InstancedMesh =>
                child instanceof THREE.InstancedMesh,
        )

        rootGroup.add(iconGroup)
        iconMeshesByType.set(type, iconMeshes)
        instanceIndexesByType.set(type, indicesForType)
    }

    const activeBursts: ParticleBurst[] = []

    const activeFlashSpheres: FlashSphere[] = []

    function spawnFlashSphere(position: THREE.Vector3, color: string) {
        const geometry = new THREE.SphereGeometry(1, 10, 10)
        markForDisposal(geometry)
        const material = new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: 0.9,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            side: THREE.FrontSide,
            toneMapped: false,
        })
        markForDisposal(material)
        const mesh = new THREE.Mesh(geometry, material)
        mesh.position.copy(position)
        mesh.scale.setScalar(0.01)
        rootGroup.add(mesh)
        activeFlashSpheres.push({
            age: 0, mesh, material,
            duration: 0.25,
            maxScale: BASE_SCALE * 3,
        })
    }

    function spawnCollectParticles(instanceIndex: number, color: string) {
        const instance = instances[instanceIndex]
        const ICON_WORLD_HEIGHT = ICON_MODEL_Y * BASE_SCALE
        const idlePhaseOffset = SIMPLE_TYPE_PHASE_OFFSET[instance.type]

        const bobOffset =
            Math.sin(idleElapsed * IDLE_BOB_SPEED + idlePhaseOffset) * IDLE_BOB_AMPLITUDE
        const spawnPos = instance.worldPosition.clone()
        spawnPos.y += ICON_WORLD_HEIGHT + bobOffset

        const count = PARTICLE_SETTINGS.count
        const positions = new Float32Array(count * 3)
        const velocities = new Float32Array(count * 3)
        const geometry = new THREE.BufferGeometry()
        markForDisposal(geometry)
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

        for (let i = 0; i < count; i++) {
            const i3 = i * 3
            _particleDir
                .set(
                    THREE.MathUtils.randFloatSpread(2),
                    Math.random() + PARTICLE_SETTINGS.upwardBias,
                    THREE.MathUtils.randFloatSpread(2),
                )
                .normalize()
                .multiplyScalar(THREE.MathUtils.randFloat(0.4, 1.0) * PARTICLE_SETTINGS.spread)
            velocities[i3] = _particleDir.x
            velocities[i3 + 1] = _particleDir.y
            velocities[i3 + 2] = _particleDir.z
        }

        const material = new THREE.PointsMaterial({
            color,
            size: PARTICLE_SETTINGS.size,
            map: getDustSpriteTexture(),
            transparent: true,
            opacity: PARTICLE_SETTINGS.opacity,
            depthWrite: false,
            blending: THREE.NormalBlending,
            sizeAttenuation: true,
            toneMapped: false,
        })
        markForDisposal(material)

        const points = new THREE.Points(geometry, material)
        points.position.copy(spawnPos)
        rootGroup.add(points)
        activeBursts.push({ age: 0, points, geometry, material, positions, velocities })

        const flashPos = instance.worldPosition.clone()
        flashPos.y += ICON_MODEL_Y * BASE_SCALE
        spawnFlashSphere(flashPos, color)
    }

    return {
        group: rootGroup,
        instances,
        iconMeshesByType,
        instanceIndexesByType,
        spawnCollectParticles,
        update(trackPosition: number, delta: number) {
            idleElapsed += delta

            // Partikel
            for (let i = activeBursts.length - 1; i >= 0; i--) {
                const burst = activeBursts[i]
                burst.age += delta
                const t = burst.age / PARTICLE_SETTINGS.lifetime
                if (t >= 1) {
                    rootGroup.remove(burst.points)
                    burst.geometry.dispose()
                    burst.material.dispose()
                    activeBursts.splice(i, 1)
                    continue
                }
                const drag = Math.exp(-PARTICLE_SETTINGS.drag * delta)
                for (let p = 0; p < burst.positions.length; p += 3) {
                    burst.positions[p] += burst.velocities[p] * PARTICLE_SETTINGS.speed * delta
                    burst.positions[p + 1] += burst.velocities[p + 1] * PARTICLE_SETTINGS.speed * delta
                    burst.positions[p + 2] += burst.velocities[p + 2] * PARTICLE_SETTINGS.speed * delta
                    burst.velocities[p] *= drag
                    burst.velocities[p + 1] *= drag
                    burst.velocities[p + 2] *= drag
                }
                burst.geometry.attributes.position.needsUpdate = true
                burst.material.opacity = PARTICLE_SETTINGS.opacity * (1 - t)
                burst.material.size = PARTICLE_SETTINGS.size
            }

            for (let i = activeFlashSpheres.length - 1; i >= 0; i--) {
                const flash = activeFlashSpheres[i]
                flash.age += delta
                const t = flash.age / flash.duration
                if (t >= 1) {
                    rootGroup.remove(flash.mesh)
                    flash.mesh.geometry.dispose()
                    flash.material.dispose()
                    activeFlashSpheres.splice(i, 1)
                    continue
                }
                const scaleT = 1 - Math.pow(1 - t, 3)
                flash.mesh.scale.setScalar(flash.maxScale * scaleT)
                flash.material.opacity = 0.9 * Math.pow(1 - t, 1.5)
            }

            for (const type of SIMPLE_TYPES) {
                const iconMeshes = iconMeshesByType.get(type) ?? []
                const globalIndices = instanceIndexesByType.get(type) ?? []
                const idlePhaseOffset = SIMPLE_TYPE_PHASE_OFFSET[type]
                const idleBobOffset =
                    Math.sin(idleElapsed * IDLE_BOB_SPEED + idlePhaseOffset) * IDLE_BOB_AMPLITUDE

                globalIndices.forEach((globalIndex, typeIndex) => {
                    const instance = instances[globalIndex]
                    const idleRotation =
                        SIMPLE_IDLE_ROTATION_OFFSET +
                        Math.sin(
                            idleElapsed * IDLE_ROTATION_SPEED +
                                idlePhaseOffset -
                                instance.position * IDLE_TRACK_ROTATION_OFFSET,
                        ) * SIMPLE_IDLE_ROTATION_SWING

                    const delta_track = trackController.positionDeltaOnTrack(
                        instance.position,
                        trackPosition,
                    )
                    const blend = THREE.MathUtils.inverseLerp(
                        powerUpScaleStartDistance,
                        powerUpScaleEndDistance,
                        delta_track,
                    )
                    const fadeScale = THREE.MathUtils.smoothstep(blend, 0, 1) * BASE_SCALE

                    if (instance.collectTimer === -1 && fadeScale <= 0) {
                        matrix.compose(
                            instance.worldPosition,
                            instance.worldRotation,
                            scale.setScalar(0),
                        )
                        iconMeshes.forEach(m => m.setMatrixAt(typeIndex, matrix))
                        return
                    }

                    if (instance.collectTimer === -2) {
                        matrix.compose(instance.worldPosition, instance.worldRotation, scale.setScalar(0))
                        iconMeshes.forEach(m => m.setMatrixAt(typeIndex, matrix))

                    } else if (instance.collectTimer >= 0) {
                        instance.collectTimer += delta
                        const progress = instance.collectTimer / SIMPLE_COLLECT_DURATION

                        if (progress >= 1.0) {
                            instance.collectTimer = -2
                            matrix.compose(instance.worldPosition, instance.worldRotation, scale.setScalar(0))
                            iconMeshes.forEach(m => m.setMatrixAt(typeIndex, matrix))
                        } else {
                            const finalScale = collectAnimScale(progress)
                            yRotation.setFromAxisAngle(
                                Y_AXIS,
                                Math.PI * 2 * COLLECT_ROTATION_TURNS * Math.pow(progress, 3),
                            )

                            _iconPosition.copy(instance.worldPosition)
                            finalRotation.copy(instance.worldRotation).multiply(yRotation)
                            matrix.compose(_iconPosition, finalRotation, scale.setScalar(finalScale))
                            iconMeshes.forEach(m => m.setMatrixAt(typeIndex, matrix))
                        }

                    } else {
                        yRotation.setFromAxisAngle(Y_AXIS, idleRotation)

                        _iconPosition.copy(instance.worldPosition)
                        _iconPosition.y += idleBobOffset
                        finalRotation.copy(instance.worldRotation).multiply(yRotation)
                        matrix.compose(_iconPosition, finalRotation, scale.setScalar(fadeScale))
                        iconMeshes.forEach(m => m.setMatrixAt(typeIndex, matrix))
                    }
                })

                iconMeshes.forEach(m => { m.instanceMatrix.needsUpdate = true })
            }
        },
    }
}
